import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, RotateCw, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import type { RuntimeConfig } from '../types';

function formatUptime(seconds: number): string {
  const s = Math.floor(seconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  parts.push(`${secs}s`);
  return parts.join(' ');
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s}s`;
  return `${(s / 60).toFixed(1)}min`;
}

function ProviderRow({
  name,
  configured,
  masked,
}: {
  name: string;
  configured: boolean;
  masked: string | null;
}) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <div className="flex items-center gap-2">
        {configured ? (
          <CheckCircle2 size={14} className="text-green-600" />
        ) : (
          <XCircle size={14} className="text-text-subtle" />
        )}
        <span className="text-sm font-medium capitalize">{name}</span>
      </div>
      <code className="text-xs text-text-muted font-mono">
        {configured ? masked : 'não configurado'}
      </code>
    </div>
  );
}

function KvRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border last:border-0">
      <span className="text-xs text-text-muted">{label}</span>
      <span className="text-xs font-mono text-right break-all">{value}</span>
    </div>
  );
}

export function RuntimeConfigPage() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);
  const [reloadResult, setReloadResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const c = await api.getRuntimeConfig();
      setConfig(c);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Falha ao carregar';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reloadCache = useCallback(async () => {
    setReloading(true);
    setReloadResult(null);
    try {
      const r = await api.reloadRuntimeConfig();
      if (r.success) {
        setReloadResult(`OK — hidratado em ${new Date(r.hydratedAt!).toLocaleTimeString()}`);
        await load();
      } else {
        setReloadResult(`Erro: ${r.error || 'desconhecido'}`);
      }
    } catch (err) {
      setReloadResult(err instanceof Error ? err.message : 'Falha');
    } finally {
      setReloading(false);
    }
  }, [load]);

  if (loading && !config) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        <Loader2 size={20} className="animate-spin mr-2" />
        Carregando configurações…
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <p className="text-red-600 text-sm mb-3">{error}</p>
        <button type="button" onClick={load} className="btn">
          <RefreshCw size={14} /> tentar de novo
        </button>
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold">Configuração vigente</h1>
          <p className="text-xs text-text-muted mt-0.5">
            O que o agent está usando agora em memória. Fonte: <code>{config.source}</code>.
          </p>
        </div>
        <button type="button" onClick={load} className="btn" disabled={loading}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Recarregar
        </button>
      </div>

      {/* Provider cache */}
      <section className="card mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-medium text-sm">Provedores de IA</h2>
            <p className="text-xs text-text-muted">
              {config.providerCache.ready
                ? `hidratado ${config.providerCache.hydratedAt ? new Date(config.providerCache.hydratedAt).toLocaleString() : '(?)'}`
                : 'cache não hidratado ainda'}
            </p>
          </div>
          <button
            type="button"
            onClick={reloadCache}
            disabled={reloading}
            className="btn text-xs"
            title="Força re-fetch do Studio"
          >
            <RotateCw size={12} className={reloading ? 'animate-spin' : ''} />
            re-hidratar do Studio
          </button>
        </div>

        <div>
          <ProviderRow
            name="anthropic"
            configured={config.providerCache.providers.anthropic.configured}
            masked={config.providerCache.providers.anthropic.masked}
          />
          <ProviderRow
            name="openai"
            configured={config.providerCache.providers.openai.configured}
            masked={config.providerCache.providers.openai.masked}
          />
          <ProviderRow
            name="google"
            configured={config.providerCache.providers.google.configured}
            masked={config.providerCache.providers.google.masked}
          />
        </div>

        {reloadResult && (
          <p className="text-xs mt-3 text-text-muted">{reloadResult}</p>
        )}

        <a
          href="https://studio.hub-platform.com/admin/providers"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-3"
        >
          Editar no Studio <ExternalLink size={11} />
        </a>
      </section>

      {/* Transcrição */}
      <section className="card mb-4">
        <h2 className="font-medium text-sm mb-3">Transcrição de áudio</h2>
        <KvRow label="provider" value={config.providerCache.transcription.provider} />
        <KvRow
          label="api key"
          value={
            config.providerCache.transcription.configured ? (
              <span className="text-green-600">
                {config.providerCache.transcription.masked}
              </span>
            ) : (
              <span className="text-text-subtle">não configurado</span>
            )
          }
        />
      </section>

      {/* Agent meta */}
      <section className="card mb-4">
        <h2 className="font-medium text-sm mb-3">Runtime do agent</h2>
        <KvRow label="engine" value={config.agent.engine} />
        <KvRow label="uptime" value={formatUptime(config.agent.uptime)} />
        <KvRow label="memória RSS" value={formatBytes(config.agent.memoryRss)} />
        <KvRow label="sessões ativas" value={config.agent.activeSessions} />
        <KvRow label="node" value={config.agent.nodeVersion} />
      </section>

      {/* Environment */}
      <section className="card mb-4">
        <h2 className="font-medium text-sm mb-3">Variáveis de ambiente</h2>
        <KvRow label="STUDIO_API_URL" value={config.env.studioApiUrl} />
        <KvRow label="PORT" value={config.env.port} />
        <KvRow label="PROJECTS_PATH" value={config.env.projectsPath} />
        <KvRow label="UPLOADS_PATH" value={config.env.uploadsPath} />
        <KvRow label="SESSION_STORE_DIR" value={config.env.sessionStoreDir} />
        <KvRow
          label="AUDIT_ENABLED"
          value={config.env.auditEnabled ? 'true' : 'false'}
        />
        <KvRow
          label="JWT_SECRET"
          value={
            config.env.jwtSecretConfigured ? (
              <span className="text-green-600">configurado</span>
            ) : (
              <span className="text-red-600">faltando</span>
            )
          }
        />
        <KvRow
          label="COPILOT_SECRET / VM_API_SECRET"
          value={
            config.env.copilotSecretConfigured ? (
              <span className="text-green-600">configurado</span>
            ) : (
              <span className="text-red-600">faltando</span>
            )
          }
        />
        <KvRow label="GIT_USER_NAME" value={config.env.gitUserName} />
        <KvRow label="GIT_USER_EMAIL" value={config.env.gitUserEmail} />
        <KvRow
          label="AUTO_COMMIT_INTERVAL"
          value={formatMs(config.env.autoCommitIntervalMs)}
        />
        <KvRow
          label="SESSION_TIMEOUT"
          value={formatMs(config.env.sessionTimeoutMs)}
        />
      </section>

      <p className="text-xs text-text-subtle italic">
        Chaves são exibidas mascaradas (primeiros 4 e últimos 4 caracteres). O agent não persiste
        chaves localmente — tudo vem do Postgres do Studio via{' '}
        <code>x-copilot-secret</code>.
      </p>
    </div>
  );
}
